import { useState, type ReactElement } from "react";
import {
  KpiCardParamsSchema,
  type KpiCardParams,
} from "@playbookai/widget-schemas";
import { Sparkline } from "../../atoms/Sparkline/Sparkline.js";
import type { WidgetBodyProps } from "../types.js";

/** Latest numeric reading — the typed `value` if numeric, else the last of
 *  `recentValues`. Drives the target tone. */
function numericValue(data: KpiCardParams): number | null {
  const trimmed = data.value.trim();
  if (trimmed !== "") {
    const n = Number(trimmed.replace(/[^0-9.-]/g, ""));
    if (Number.isFinite(n)) return n;
  }
  const rv = data.recentValues;
  return rv.length > 0 ? rv[rv.length - 1]! : null;
}

/** Sparkline tone vs target (mirrors SOLVE's KpiTile): healthy on/above target
 *  (respecting `higherIsBetter`), watch when off, neutral when no target. */
function sparkTone(data: KpiCardParams): "healthy" | "watch" | "neutral" {
  if (data.target == null) return "neutral";
  const v = numericValue(data);
  if (v == null) return "neutral";
  const onTarget = data.higherIsBetter ? v >= data.target : v <= data.target;
  return onTarget ? "healthy" : "watch";
}

/**
 * KPI tile body — Hoshin canvas Yjs widget (PB-234). Inline-edits the plan's KPI
 * contract — `label`, `value`, `unit`, `target`, and `higherIsBetter` direction —
 * and shows the `recentValues` history as a read-only sparkline (fed by data,
 * not hand-typed; the same treatment as SOLVE's KPI tile). `sourceText` is
 * intentionally NOT surfaced. Each commit is guarded by
 * `KpiCardParamsSchema.safeParse` before it reaches Yjs (the Yjs render path does
 * no schema validation). Card frame + drag header come from the adapter.
 */
export function KpiCardWidget({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<KpiCardParams>): ReactElement {
  const commit = (next: Partial<KpiCardParams>): void => {
    const parsed = KpiCardParamsSchema.safeParse({ ...data, ...next });
    if (parsed.success) onChange(parsed.data);
  };
  // Local draft for the target field so intermediate states (`90.`, `-`) survive
  // while typing — the committed value is numeric, so a controlled input bound
  // straight to `data.target` would strip the trailing dot / lone minus and
  // corrupt normal decimal/negative entry. Cleared on blur to resync to `data`.
  const [targetDraft, setTargetDraft] = useState<string | null>(null);
  const targetValue =
    targetDraft ?? (data.target == null ? "" : String(data.target));
  const onTargetInput = (raw: string): void => {
    setTargetDraft(raw);
    const trimmed = raw.trim();
    if (trimmed === "") {
      commit({ target: undefined });
      return;
    }
    const n = Number(trimmed);
    if (Number.isFinite(n)) commit({ target: n });
  };

  return (
    <div className="flex h-full w-full flex-col gap-1 p-3">
      {/* eslint-disable-next-line ds/no-hand-built -- kept: a bare in-card field that takes the card's own type; Input ghost adds a hover wash and a 1px edge, and no approved look row lists the change. */}
      <input
        value={data.label}
        disabled={readOnly}
        onChange={(e) => commit({ label: e.target.value })}
        placeholder="KPI label"
        aria-label="KPI label"
        className="truncate rounded-md bg-transparent text-dense-2 uppercase tracking-wider text-pb-text-muted focus-field placeholder:text-pb-text-muted disabled:cursor-default"
      />
      <div className="flex items-baseline gap-2">
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a bare in-card field that takes the card's own type; Input ghost adds a hover wash and a 1px edge, and no approved look row lists the change. */}
        <input
          value={data.value}
          disabled={readOnly}
          onChange={(e) => commit({ value: e.target.value })}
          placeholder="0"
          aria-label="KPI value"
          className="min-w-0 flex-1 rounded-md bg-transparent text-3xl font-semibold tabular-nums leading-none text-pb-text focus-field placeholder:text-pb-text-muted disabled:cursor-default"
        />
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a bare in-card field that takes the card's own type; Input ghost adds a hover wash and a 1px edge, and no approved look row lists the change. */}
        <input
          value={data.unit ?? ""}
          disabled={readOnly}
          onChange={(e) => commit({ unit: e.target.value })}
          placeholder="unit"
          aria-label="KPI unit"
          className="w-16 shrink-0 rounded-md bg-transparent text-end text-dense-1 text-pb-text-muted focus-field placeholder:text-pb-text-muted disabled:cursor-default"
        />
      </div>
      <div className="flex items-center gap-2 text-dense-1">
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a 20px target field with a 4px corner; NumberInput size="sm" is a 28px field with the 8px corner, and no approved look row lists the change. */}
        <input
          value={targetValue}
          disabled={readOnly}
          onChange={(e) => onTargetInput(e.target.value)}
          onBlur={() => setTargetDraft(null)}
          inputMode="decimal"
          placeholder="target"
          aria-label="KPI target"
          className="w-20 rounded border border-pb-control-border bg-pb-card px-1.5 py-0.5 tabular-nums text-pb-text placeholder:text-pb-text-muted focus-frame disabled:cursor-default"
        />
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a 20px outlined toggle with a 4px corner whose label flips; Button pressed would add the teal selection, and no approved look row lists either change. */}
        <button
          type="button"
          disabled={readOnly}
          onClick={() => commit({ higherIsBetter: !data.higherIsBetter })}
          aria-label="Toggle higher-is-better direction"
          aria-pressed={data.higherIsBetter}
          className="inline-flex items-center gap-1 rounded border border-pb-control-border px-1.5 py-0.5 text-pb-text-muted transition-colors hover:text-pb-text disabled:cursor-default"
        >
          {data.higherIsBetter ? "↑ Higher" : "↓ Lower"} is better
        </button>
      </div>
      <div className="mt-auto min-h-0 pt-1">
        {data.recentValues.length >= 2 ? (
          <Sparkline
            data={data.recentValues}
            tone={sparkTone(data)}
            fill
            width={260}
            height={36}
            className="h-9 w-full"
          />
        ) : (
          <span className="text-dense-1 text-pb-text-muted">
            Trend appears with recent values
          </span>
        )}
      </div>
    </div>
  );
}
